import { useForm } from 'react-hook-form';
import { FC } from 'react';

import {
  Box,
  Button,
  Center,
  Flex,
  FormControl,
  FormErrorIcon,
  FormErrorMessage,
  FormLabel,
  Heading,
  Input,
  Link,
  VStack,
} from '@chakra-ui/react';
import { Link as ReactLink } from 'react-router-dom';

import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { StJude } from '../../assets';
import { Notification } from '../notification';
import { getDataTracking } from '../../utils';

const schema = yup.object().shape({
  userName: yup.string().required('A username is required'),
  password: yup.string().required('A password is required'),
});

interface LoginFormProps {
  onLogin: (values: any) => void;
  onForgotUsername?: () => void;
  onForgotPassword?: () => void;
  onCreateAccount?: () => void;
  errorMessage: string;
  isLoading: boolean;
}

export const LoginForm: FC<LoginFormProps> = ({
  onLogin,
  onCreateAccount,
  errorMessage,
  isLoading,
}: LoginFormProps): JSX.Element => {
  const {
    handleSubmit,
    formState: { errors },
    register,
  } = useForm({ resolver: yupResolver(schema) });

  const getError = (field: string): any => errors[field]?.message;
  const hasError = (field: string): boolean => !!getError(field);

  const linkColor = 'brand.secondary.swatch4';
  const buttonColorScheme = 'blue';

  const handleLogin = (values: any) => {
    onLogin(values);
  };

  return (
    <Box>
      <Flex
        flexDirection="column"
        w={['100%', '288px', '384px']}
        p={['1.25rem', '0px']}
        m="0 auto"
      >
        <Box mb="2rem" mt="2rem">
          <Center>
            <StJude width="100px" />
          </Center>
          <Flex flexDirection="column">
            <Center>
              <Heading size="md">Log in</Heading>
            </Center>
            <Center>
              <Heading size="sm" mt="1rem">
                with your St. Jude account
              </Heading>
            </Center>
          </Flex>
        </Box>

        <form onSubmit={handleSubmit(handleLogin)}>
          <VStack alignItems="left" spacing="20px" pb="30px">
            <Box>
              <FormControl isInvalid={hasError('userName')}>
                <Flex justifyContent="space-between">
                  <FormLabel htmlFor="userName">Username</FormLabel>
                  <Link
                    as={ReactLink}
                    color={linkColor}
                    tabIndex={-1}
                    textDecoration="none"
                    to="/recover?type=username"
                    {...getDataTracking('forgotUsernameLink')}
                  >
                    Forgot Username?
                  </Link>
                </Flex>
                <Input
                  focusBorderColor="blue"
                  id="userName"
                  w="100%"
                  {...register('userName')}
                />
                <FormErrorMessage>
                  <FormErrorIcon />
                  {getError('userName')}
                </FormErrorMessage>
              </FormControl>
            </Box>
            <Box>
              <FormControl isInvalid={hasError('password')}>
                <Flex justifyContent="space-between">
                  <FormLabel htmlFor="password">Password</FormLabel>
                  <Link
                    as={ReactLink}
                    color={linkColor}
                    tabIndex={-1}
                    textDecoration="none"
                    to="/recover?type=password"
                    {...getDataTracking('forgotPasswordLink')}
                  >
                    Forgot Password?
                  </Link>
                </Flex>
                <Input
                  id="password"
                  type="password"
                  w="100%"
                  {...register('password')}
                />
                <FormErrorMessage>
                  <FormErrorIcon />
                  {getError('password')}
                </FormErrorMessage>
              </FormControl>
            </Box>
            <Button
              colorScheme={buttonColorScheme}
              size="lg"
              w="100%"
              type="submit"
              isLoading={isLoading}
              {...getDataTracking('loginButton')}
            >
              Login
            </Button>
            {onCreateAccount && (
              <Button
                {...getDataTracking('createAccountButton')}
                onClick={onCreateAccount}
                colorScheme={buttonColorScheme}
                size="lg"
                w="100%"
              >
                Create an Account
              </Button>
            )}
          </VStack>
        </form>
        <Box>
          {errorMessage && (
            <Notification message={errorMessage} status="error" hasClose />
          )}
        </Box>
      </Flex>
    </Box>
  );
};
